import React, { useEffect, useState } from "react";
import apiClient from "../../lib/api";
import { useSettings } from "../../contexts/SettingsContext";
import { useI18n } from "../../i18n";
import { toast } from "sonner";
import { Save, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

const empty = { label_en: "", label_nl: "", href: "/shop" };

const AdminNavigation = () => {
  const { lang } = useI18n();
  const { refresh } = useSettings();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/settings").then((r) => setItems(r.data?.navigation?.primary || []));
  }, []);

  const upd = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => setItems((arr) => [...arr, { ...empty }]);
  const remove = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const move = (i, dir) => setItems((arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const copy = [...arr];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const save = async () => {
    setBusy(true);
    try {
      await apiClient.put("/admin/settings", { navigation: { primary: items } });
      toast.success(lang === "nl" ? "Opgeslagen" : "Saved");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-navigation-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{lang === "nl" ? "Navigatie" : "Navigation"}</h1>
          <div className="font-label text-ink-muted mt-1">{lang === "nl" ? "Bewerk de hoofdmenu items van je winkel" : "Edit the primary menu items of your store"}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={add} className="btn-secondary text-sm" data-testid="nav-add"><Plus className="w-4 h-4" /> {lang === "nl" ? "Item" : "Item"}</button>
          <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="nav-save"><Save className="w-4 h-4" /> {busy ? "…" : (lang === "nl" ? "Opslaan" : "Save")}</button>
        </div>
      </div>

      <div className="border border-black/10 bg-white">
        <div className="grid grid-cols-[40px_1fr_1fr_1.5fr_120px] gap-3 px-4 py-3 border-b border-black/5 font-label text-ink-muted text-xs">
          <div>#</div>
          <div>Label EN</div>
          <div>Label NL</div>
          <div>{lang === "nl" ? "URL (intern: /shop · extern: https://)" : "URL (internal: /shop · external: https://)"}</div>
          <div className="text-right">{lang === "nl" ? "Acties" : "Actions"}</div>
        </div>
        {items.length === 0 && <div className="p-8 text-center text-ink-muted text-sm">{lang === "nl" ? "Nog geen items. Klik op 'Item' om toe te voegen." : "No items. Click 'Item' to add one."}</div>}
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-[40px_1fr_1fr_1.5fr_120px] gap-3 px-4 py-3 border-b border-black/5 last:border-0 items-center" data-testid={`nav-row-${i}`}>
            <div className="font-mono text-xs text-ink-muted">{i + 1}</div>
            <input data-testid={`nav-label-en-${i}`} value={it.label_en} onChange={(e) => upd(i, "label_en", e.target.value)} placeholder="Home" className="border border-black/15 px-3 py-2 text-sm bg-white" />
            <input data-testid={`nav-label-nl-${i}`} value={it.label_nl} onChange={(e) => upd(i, "label_nl", e.target.value)} placeholder="Home" className="border border-black/15 px-3 py-2 text-sm bg-white" />
            <input data-testid={`nav-href-${i}`} value={it.href} onChange={(e) => upd(i, "href", e.target.value)} placeholder="/shop?category=..." className="border border-black/15 px-3 py-2 text-sm bg-white font-mono" />
            <div className="flex justify-end gap-1">
              <button onClick={() => move(i, -1)} className="p-2 hover:bg-secondary-warm" title="Up"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => move(i, 1)} className="p-2 hover:bg-secondary-warm" title="Down"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button data-testid={`nav-delete-${i}`} onClick={() => remove(i)} className="p-2 hover:bg-terracotta/10 text-terracotta" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 text-xs text-ink-muted">{lang === "nl" ? "Tip: gebruik /shop?category=<slug> voor categorieën. Externe links beginnen met https://" : "Tip: use /shop?category=<slug> for categories. External links start with https://"}</div>
    </div>
  );
};

export default AdminNavigation;
